import { trpc } from "@api/client";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import dispatchToast from "@utils/dispatch-toast";
import { useState } from "react";
import * as yup from "yup";
import { REQUIRED_FORM_FIELD_ERROR } from "../constants";
import { useSetupSummary } from "../context/setup-summary-context";
import { JobRole } from "../types";

export const VALIDATION_SCHEMA = yup.object({
  cbo: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
  name: yup.string().required(REQUIRED_FORM_FIELD_ERROR),
});

export function useCreateJobRoleDrawer({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (jobRole: JobRole) => void;
}) {
  const utils = trpc.useUtils();
  const { incrementJobRolesCreated } = useSetupSummary();

  const { selectedCompany } = useSelectedCompany();

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [cbo, setCbo] = useState<string>("");
  const [errors, setErrors] = useState<{
    cbo?: string;
    name?: string;
  }>({});

  const [isCreateJobRoleLoading, setIsCreateJobRoleLoading] = useState(false);

  const { mutateAsync: createJobRole } = trpc.createJobRole.useMutation();

  const resetForm = () => {
    setName("");
    setDescription("");
    setCbo("");
    setErrors({});
  };

  const handleDrawerClose = () => {
    onClose();
    resetForm();
  };

  const handleCboBlur = () => {
    if (!cbo) {
      setErrors((prev) => ({ ...prev, cbo: REQUIRED_FORM_FIELD_ERROR }));
    } else {
      setErrors((prev) => ({ ...prev, cbo: undefined }));
    }
  };

  const handleCboChange = (value: string) => {
    setCbo(value);
    setErrors((prev) => ({ ...prev, cbo: undefined }));
  };

  const handleFindCboClick = () =>
    window.open(
      "https://cbo.mte.gov.br/cbosite/pages/pesquisas/BuscaPorTitulo.jsf",
      "_blank"
    );

  const handleNameBlur = () => {
    if (!name) {
      setErrors((prev) => ({ ...prev, name: REQUIRED_FORM_FIELD_ERROR }));
    } else {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setErrors((prev) => ({ ...prev, name: undefined }));
  };

  const handleCreateJobRole = async () => {
    setIsCreateJobRoleLoading(true);
    try {
      await VALIDATION_SCHEMA.validate(
        {
          cbo,
          name,
        },
        { abortEarly: false }
      );

      const newJobRole = await createJobRole({
        companyId: selectedCompany.id,
        name,
        description,
        cbo,
      });

      const queryInput = { companyId: selectedCompany.id };

      const newJobRoles = await utils.client.getJobRoles.query(queryInput);

      utils.getJobRoles.setData(queryInput, newJobRoles);

      onSuccess(newJobRole);

      dispatchToast({
        content: "Cargo criado com sucesso!",
        type: "success",
      });

      incrementJobRolesCreated();

      setIsCreateJobRoleLoading(false);

      handleDrawerClose();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const newErrors = error.inner.reduce((acc, curr) => {
          if (curr.path) acc[curr.path] = curr.message;
          return acc;
        }, {} as Record<string, string>);
        setErrors(newErrors);
      }
      setIsCreateJobRoleLoading(false);
    }
  };

  return {
    cbo,
    cboError: errors.cbo,
    name,
    nameError: errors.name,
    description,
    isLoading: isCreateJobRoleLoading,

    onCboBlur: handleCboBlur,
    onCboChange: handleCboChange,
    onDrawerSubmit: handleCreateJobRole,
    onDescriptionChange: setDescription,
    onDrawerClose: handleDrawerClose,
    onFindCboClick: handleFindCboClick,
    onNameBlur: handleNameBlur,
    onNameChange: handleNameChange,
  };
}
